import { useState, useEffect, useRef, useCallback } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { Settings } from 'lucide-react';
import './TimezoneClock.css';

// NATO time zone letters by UTC offset
const NATO_LETTERS: Record<number, string> = {
  0: 'Z', 1: 'A', 2: 'B', 3: 'C', 4: 'D', 5: 'E', 6: 'F',
  7: 'G', 8: 'H', 9: 'I', 10: 'K', 11: 'L', 12: 'M',
  '-1': 'N', '-2': 'O', '-3': 'P', '-4': 'Q', '-5': 'R',
  '-6': 'S', '-7': 'T', '-8': 'U', '-9': 'V', '-10': 'W',
  '-11': 'X', '-12': 'Y',
};

const NATO_NAMES: Record<string, string> = {
  Z: 'Zulu', A: 'Alpha', B: 'Bravo', C: 'Charlie', D: 'Delta',
  E: 'Echo', F: 'Foxtrot', G: 'Golf', H: 'Hotel', I: 'India',
  K: 'Kilo', L: 'Lima', M: 'Mike', N: 'November', O: 'Oscar',
  P: 'Papa', Q: 'Quebec', R: 'Romeo', S: 'Sierra', T: 'Tango',
  U: 'Uniform', V: 'Victor', W: 'Whiskey', X: 'X-ray', Y: 'Yankee',
};

export interface TimezoneOption {
  id: string;
  label: string;
  country: string | null;
  timezone: string;
  flagImage: string | null;
  countries: string[];
}

// Available timezone options for selection
const TIMEZONE_OPTIONS: TimezoneOption[] = [
  { id: 'utc', label: 'UTC', country: null, timezone: 'UTC', flagImage: null, countries: ['Universal Time'] },
  { id: 'gbr', label: 'GBR', country: 'GBR', timezone: 'Europe/London', flagImage: '/flags/gb.png', countries: ['United Kingdom', 'Portugal', 'Ireland'] },
  { id: 'fra', label: 'FRA', country: 'FRA', timezone: 'Europe/Paris', flagImage: '/flags/fr.png', countries: ['France', 'Belgium', 'Luxembourg'] },
  { id: 'deu', label: 'DEU', country: 'DEU', timezone: 'Europe/Berlin', flagImage: '/flags/de.png', countries: ['Germany', 'Austria', 'Switzerland'] },
  { id: 'ita', label: 'ITA', country: 'ITA', timezone: 'Europe/Rome', flagImage: '/flags/it.png', countries: ['Italy'] },
  { id: 'esp', label: 'ESP', country: 'ESP', timezone: 'Europe/Madrid', flagImage: '/flags/es.png', countries: ['Spain'] },
  { id: 'nld', label: 'NLD', country: 'NLD', timezone: 'Europe/Amsterdam', flagImage: '/flags/nl.png', countries: ['Netherlands'] },
  { id: 'bel', label: 'BEL', country: 'BEL', timezone: 'Europe/Brussels', flagImage: '/flags/be.png', countries: ['Belgium'] },
  { id: 'pol', label: 'POL', country: 'POL', timezone: 'Europe/Warsaw', flagImage: '/flags/pl.png', countries: ['Poland'] },
  { id: 'cze', label: 'CZE', country: 'CZE', timezone: 'Europe/Prague', flagImage: '/flags/cz.png', countries: ['Czech Republic'] },
  { id: 'dnk', label: 'DNK', country: 'DNK', timezone: 'Europe/Copenhagen', flagImage: '/flags/dk.png', countries: ['Denmark'] },
  { id: 'nor', label: 'NOR', country: 'NOR', timezone: 'Europe/Oslo', flagImage: '/flags/no.png', countries: ['Norway'] },
  { id: 'swe', label: 'SWE', country: 'SWE', timezone: 'Europe/Stockholm', flagImage: '/flags/se.png', countries: ['Sweden'] },
  { id: 'fin', label: 'FIN', country: 'FIN', timezone: 'Europe/Helsinki', flagImage: '/flags/fi.png', countries: ['Finland', 'Estonia', 'Latvia', 'Lithuania'] },
  { id: 'grc', label: 'GRC', country: 'GRC', timezone: 'Europe/Athens', flagImage: '/flags/gr.png', countries: ['Greece', 'Cyprus'] },
  { id: 'rou', label: 'ROU', country: 'ROU', timezone: 'Europe/Bucharest', flagImage: '/flags/ro.png', countries: ['Romania'] },
  { id: 'bgr', label: 'BGR', country: 'BGR', timezone: 'Europe/Sofia', flagImage: '/flags/bg.png', countries: ['Bulgaria'] },
  { id: 'tur', label: 'TUR', country: 'TUR', timezone: 'Europe/Istanbul', flagImage: '/flags/tr.png', countries: ['Turkey'] },
  { id: 'prt', label: 'PRT', country: 'PRT', timezone: 'Europe/Lisbon', flagImage: '/flags/pt.png', countries: ['Portugal'] },
  { id: 'est', label: 'EST', country: 'EST', timezone: 'Europe/Tallinn', flagImage: '/flags/ee.png', countries: ['Estonia'] },
  { id: 'lva', label: 'LVA', country: 'LVA', timezone: 'Europe/Riga', flagImage: '/flags/lv.png', countries: ['Latvia'] },
  { id: 'ltu', label: 'LTU', country: 'LTU', timezone: 'Europe/Vilnius', flagImage: '/flags/lt.png', countries: ['Lithuania'] },
  { id: 'hrv', label: 'HRV', country: 'HRV', timezone: 'Europe/Zagreb', flagImage: '/flags/hr.png', countries: ['Croatia'] },
  { id: 'svn', label: 'SVN', country: 'SVN', timezone: 'Europe/Ljubljana', flagImage: '/flags/si.png', countries: ['Slovenia'] },
  { id: 'svk', label: 'SVK', country: 'SVK', timezone: 'Europe/Bratislava', flagImage: '/flags/sk.png', countries: ['Slovakia'] },
  { id: 'hun', label: 'HUN', country: 'HUN', timezone: 'Europe/Budapest', flagImage: '/flags/hu.png', countries: ['Hungary'] },
  { id: 'che', label: 'CHE', country: 'CHE', timezone: 'Europe/Zurich', flagImage: '/flags/ch.png', countries: ['Switzerland'] },
  { id: 'aut', label: 'AUT', country: 'AUT', timezone: 'Europe/Vienna', flagImage: '/flags/at.png', countries: ['Austria'] },
  { id: 'usa_east', label: 'USA (E)', country: 'USA', timezone: 'America/New_York', flagImage: '/flags/us.png', countries: ['United States (Eastern)'] },
  { id: 'usa_cent', label: 'USA (C)', country: 'USA', timezone: 'America/Chicago', flagImage: '/flags/us.png', countries: ['United States (Central)'] },
  { id: 'can', label: 'CAN', country: 'CAN', timezone: 'America/Toronto', flagImage: '/flags/ca.png', countries: ['Canada (Eastern)'] },
];

const DEFAULT_SELECTED_IDS = ['utc', 'gbr', 'deu', 'fin'];
const STORAGE_KEY = 'camo-timezone-clock-selection';

function getNatoLetter(timezone: string): string {
  const now = new Date();
  const formatted = formatInTimeZone(now, timezone, 'XXX');
  if (formatted === 'Z') return 'Z';
  const sign = formatted[0] === '+' ? 1 : -1;
  const hours = parseInt(formatted.substring(1, 3), 10) * sign;
  return NATO_LETTERS[hours] || '?';
}

function getOffsetString(timezone: string): string {
  if (timezone === 'UTC') return '+0';
  const now = new Date();
  const formatted = formatInTimeZone(now, timezone, 'XXX');
  if (formatted === 'Z') return '+0';
  const hours = parseInt(formatted.substring(1, 3), 10);
  const sign = formatted[0];
  return `${sign}${hours}`;
}

function loadSelection(): string[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as string[];
      if (Array.isArray(parsed) && parsed.length >= 1 && parsed.length <= 4 && parsed.every(id => TIMEZONE_OPTIONS.find(o => o.id === id))) {
        return parsed;
      }
    }
  } catch { /* ignore */ }
  return DEFAULT_SELECTED_IDS;
}

function TimezoneClock() {
  const [selectedIds, setSelectedIds] = useState<string[]>(loadSelection);
  const [times, setTimes] = useState<Record<string, string>>({});
  const [menuOpen, setMenuOpen] = useState(false);
  const [editSelection, setEditSelection] = useState<string[]>([]);
  const menuRef = useRef<HTMLDivElement>(null);

  const updateTimes = useCallback(() => {
    const now = new Date();
    const newTimes: Record<string, string> = {};
    TIMEZONE_OPTIONS.forEach(tz => {
      newTimes[tz.id] = formatInTimeZone(now, tz.timezone, 'HH:mm');
    });
    setTimes(newTimes);
  }, []);

  useEffect(() => {
    updateTimes();
    const interval = setInterval(updateTimes, 60000);
    return () => clearInterval(interval);
  }, [updateTimes]);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const selectedOptions = selectedIds.map(id => TIMEZONE_OPTIONS.find(o => o.id === id)!);

  const openMenu = () => {
    setEditSelection([...selectedIds]);
    setMenuOpen(true);
  };

  const toggleOption = (id: string) => {
    setEditSelection(prev => {
      if (prev.includes(id)) {
        return prev.filter(x => x !== id);
      }
      if (prev.length >= 4) return prev;
      return [...prev, id];
    });
  };

  const applySelection = () => {
    if (editSelection.length < 1) return;
    setSelectedIds(editSelection);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(editSelection));
    setMenuOpen(false);
  };

  const resetToDefault = () => {
    setEditSelection([...DEFAULT_SELECTED_IDS]);
  };

  // Group options by NATO letter for the menu
  const optionsWithNato = TIMEZONE_OPTIONS.map(opt => ({
    ...opt,
    natoLetter: getNatoLetter(opt.timezone),
    offset: getOffsetString(opt.timezone),
  }));

  // Sort by offset
  optionsWithNato.sort((a, b) => {
    const aOff = parseFloat(a.offset);
    const bOff = parseFloat(b.offset);
    return aOff - bOff;
  });

  return (
    <div className="timezone-clock" ref={menuRef}>
      {selectedOptions.map(tz => {
        const natoLetter = getNatoLetter(tz.timezone);
        const natoName = NATO_NAMES[natoLetter] || '';
        return (
          <div
            key={tz.id}
            className="tz-item"
            title={`${tz.label} — UTC${getOffsetString(tz.timezone)} — ${natoLetter} (${natoName})\n${tz.countries.join(', ')}`}
          >
            <div className="tz-row-top">
              {tz.flagImage ? (
                <img src={tz.flagImage} alt={tz.country || ''} className="tz-flag" />
              ) : (
                <span className="tz-label-zulu">Z</span>
              )}
              <span className="tz-label">{tz.label}</span>
            </div>
            <div className="tz-row-bottom">
              <span className="tz-time">{times[tz.id] || '--:--'}</span>
              <span className="tz-nato">{natoLetter}</span>
            </div>
          </div>
        );
      })}

      <button className="tz-settings-btn" onClick={openMenu} title="Configure time zones">
        <Settings size={13} />
      </button>

      {menuOpen && (
        <div className="tz-menu">
          <div className="tz-menu-header">
            <span>Select 1–4 time zones</span>
            <span className="tz-menu-count">{editSelection.length}/4</span>
          </div>
          <div className="tz-menu-list">
            {optionsWithNato.map(opt => {
              const isSelected = editSelection.includes(opt.id);
              const isDisabled = !isSelected && editSelection.length >= 4;
              return (
                <button
                  key={opt.id}
                  className={`tz-menu-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                  onClick={() => toggleOption(opt.id)}
                  disabled={isDisabled}
                >
                  <span className="tz-menu-check">{isSelected ? '✓' : ''}</span>
                  {opt.flagImage ? (
                    <img src={opt.flagImage} alt="" className="tz-menu-flag" />
                  ) : (
                    <span className="tz-menu-zulu">Z</span>
                  )}
                  <span className="tz-menu-label">{opt.label}</span>
                  <span className="tz-menu-time">{times[opt.id] || '--:--'}</span>
                  <span className="tz-menu-nato">{opt.natoLetter}</span>
                  <span className="tz-menu-offset">UTC{opt.offset}</span>
                </button>
              );
            })}
          </div>
          <div className="tz-menu-footer">
            <button className="tz-menu-reset" onClick={resetToDefault}>Reset</button>
            <button
              className="tz-menu-apply"
              onClick={applySelection}
              disabled={editSelection.length < 1}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TimezoneClock;
